import { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import { JobPostingDetail, PaginatedResponse, RecruiterCollection } from '../types';

interface AllowedUsersMutationVariables {
  recruiter?: number;
  manager?: number;
  related?: number[];
  observers?: number[];
  jobPostingId: JobPostingDetail['id'];
}

const useAllowedUsersMutation = (
  options?: Omit<
    UseMutationOptions<void, AxiosError<unknown, unknown>, AllowedUsersMutationVariables, unknown>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useAPIMutation<void, AllowedUsersMutationVariables, unknown>(async (variables, api) => {
    let recruitersPage: PaginatedResponse<RecruiterCollection> = {
      next: undefined,
      count: 0,
      results: [],
    };

    do {
      recruitersPage = await queryClient.fetchQuery(
        recruitersPage.next ?? [
          '/recruiters/',
          {
            params: {
              job_posting_id: variables.jobPostingId,
            },
          },
        ]
      );

      for (const recruiter of recruitersPage.results) {
        await api.delete(`/recruiters/${recruiter.id}/`);
      }
    } while (recruitersPage.next);

    if (variables.recruiter) {
      await api.post('/recruiters/', {
        job_posting: variables.jobPostingId,
        user: variables.recruiter,
        type: 'RECRUITER',
      });
    }

    if (variables.manager) {
      await api.post('/recruiters/', {
        job_posting: variables.jobPostingId,
        user: variables.manager,
        type: 'MANAGER',
      });
    }

    for (const related of variables.related || []) {
      await api.post('/recruiters/', {
        job_posting: variables.jobPostingId,
        user: related,
        type: 'RELATED',
      });
    }

    for (const observer of variables.observers || []) {
      await api.post('/recruiters/', {
        job_posting: variables.jobPostingId,
        user: observer,
        type: 'OBSERVER',
      });
    }
  }, options);
};

export default useAllowedUsersMutation;
